// Overview — cash / cards / A-R / MTD-net tiles + aging bar + most-overdue,
// and (Financials v2 slice 1) the bank data: an attention row under the tiles
// and the Bank vs QuickBooks panel. bank_status is read on its own: its error
// shows inside that panel, never in place of the page.
import React, { useState } from "react";
import { C, font, mono } from "../../../lib/styles.js";
import { useQbo } from "../lib/useQbo.js";
import { bankOverview } from "../lib/bankData.js";
import { BankVsBooksPanel, bankAttentionItem } from "./BankParts.jsx";
import UnmatchedLists from "./UnmatchedLists.jsx";
import {
  Attn, Badge, ErrorPanel, Loading, RefreshButton, lblStyle, money, monthStart,
  panel, td, tdNum, today,
} from "./common.jsx";

const statTile = { ...panel, padding: 14 };
const statVal = { fontSize: 20, fontWeight: 400, marginTop: 6, fontFamily: mono };
const statSub = { fontSize: 11, color: C.muted, marginTop: 3 };

const AGING_COLORS = ["#10B981", "#F59E0B", "#F97316", "#EF4444", "#B91C1C"];

export default function Overview({ ctx, onGoSettings }) {
  const accounts = useQbo(ctx, "get_accounts");
  const aging = useQbo(ctx, "report_ar_aging");
  const invoices = useQbo(ctx, "list_open_invoices");
  const pnl = useQbo(ctx, "report_pnl", { start: monthStart(), end: today() });
  const bank = useQbo(ctx, "bank_status");
  const [openBank, setOpenBank] = useState([]); // accountIds expanded in Bank vs QuickBooks

  if (accounts.loading && !accounts.data) return <Loading label="Loading the books…" />;
  // Any section failing must surface as an ERROR, never as a reassuring
  // empty state ("Nothing overdue" on a failed invoice fetch — codex review).
  // bank_status is deliberately not here: its error stays in its own panel.
  const firstError = accounts.error || aging.error || invoices.error || pnl.error;
  const refreshAll = () => { accounts.refresh(); aging.refresh(); invoices.refresh(); pnl.refresh(); bank.refresh(); };
  if (firstError) return <ErrorPanel error={firstError} onRetry={refreshAll} />;

  const bankView = bank.data && !bank.error ? bankOverview(bank.data) : null;
  const toggleBank = (id) => setOpenBank((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  const showBankDetails = () => setOpenBank((ids) => [...new Set([...ids, ...(bankView?.attention?.accountIds ?? [])])]);

  const accts = accounts.data?.accounts ?? [];
  const sum = (pred) => accts.filter(pred).reduce((s, a) => s + (a.currentBalance ?? 0), 0);
  const cash = sum((a) => a.accountType === "Bank");
  const cards = sum((a) => a.accountType === "Credit Card");
  const totalAR = aging.data?.totalAR ?? sum((a) => a.accountType === "Accounts Receivable");
  const net = pnl.data?.netIncome;

  const buckets = (aging.data?.buckets ?? []).filter((b) => b.amount !== null);
  // The last aging column is usually the Total — drop it from the bar, and
  // size segments against the REMAINING buckets only (codex review: summing
  // the total in made every segment render at half width).
  const barBuckets = buckets.length > 1 ? buckets.slice(0, -1) : buckets;
  const bucketSum = barBuckets.reduce((s, b) => s + Math.max(0, b.amount ?? 0), 0);

  const overdue = (invoices.data?.invoices ?? [])
    .filter((i) => i.overdueDays > 0)
    .sort((a, b) => b.overdueDays - a.overdueDays)
    .slice(0, 5);

  return (
    <div style={{ fontFamily: font }}>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
        <RefreshButton onClick={refreshAll} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 16 }}>
        <div style={statTile}>
          <span style={lblStyle}>Cash in bank</span>
          <div style={statVal}>{money(cash, false)}</div>
          <div style={statSub}>{accts.filter((a) => a.accountType === "Bank").length} account(s)</div>
        </div>
        <div style={statTile}>
          <span style={lblStyle}>Credit cards owed</span>
          <div style={{ ...statVal, color: cards > 0 ? C.danger : C.text }}>{money(cards, false)}</div>
          <div style={statSub}>{accts.filter((a) => a.accountType === "Credit Card").length} card(s)</div>
        </div>
        <div style={statTile}>
          <span style={lblStyle}>Accounts receivable</span>
          <div style={statVal}>{money(totalAR, false)}</div>
          <div style={statSub}>{invoices.data?.invoices?.length ?? "…"} open invoice(s)</div>
        </div>
        <div style={statTile}>
          <span style={lblStyle}>Net income · month to date</span>
          <div style={{ ...statVal, color: net > 0 ? C.green : net < 0 ? C.danger : C.text }}>
            {pnl.loading ? "…" : money(net, false)}
          </div>
          <div style={statSub}>{pnl.data?.method ?? "Accrual"}</div>
        </div>
      </div>

      {bankView?.attention ? (
        <Attn style={{ marginBottom: 16 }} items={[bankAttentionItem(bankView.attention, showBankDetails)]} />
      ) : null}

      <div style={panel}>
        <span style={lblStyle}>A/R aging</span>
        {aging.loading && !aging.data ? <Loading /> : (
          <>
            <div style={{ display: "flex", height: 14, borderRadius: 7, overflow: "hidden", margin: "10px 0 6px", background: C.surfaceAlt }}>
              {bucketSum > 0 && barBuckets.map((b, i) => (
                <div key={b.label} style={{
                  width: `${(Math.max(0, b.amount ?? 0) / bucketSum) * 100}%`,
                  background: AGING_COLORS[i % AGING_COLORS.length],
                }} />
              ))}
            </div>
            <div style={{ display: "flex", gap: 14, fontSize: 11, color: C.muted, flexWrap: "wrap" }}>
              {barBuckets.map((b, i) => (
                <span key={b.label}>
                  <span style={{
                    display: "inline-block", width: 8, height: 8, borderRadius: 2,
                    marginRight: 5, background: AGING_COLORS[i % AGING_COLORS.length],
                  }} />
                  {b.label} {money(b.amount, false)}
                </span>
              ))}
            </div>
          </>
        )}
        <div style={{ height: 1, background: C.border, margin: "16px 0" }} />
        <span style={lblStyle}>Most overdue</span>
        {invoices.loading && !invoices.data ? <Loading /> : overdue.length === 0 ? (
          <div style={{ fontSize: 12.5, color: C.muted, padding: "10px 0" }}>Nothing overdue. 🎉</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 4 }}>
            <tbody>
              {overdue.map((i) => (
                <tr key={i.id}>
                  <td style={td}>{i.docNumber ? `INV-${i.docNumber} · ` : ""}{i.customer}</td>
                  <td style={tdNum}>{money(i.balance)}</td>
                  <td style={{ ...td, width: 120 }}><Badge tone="over">{i.overdueDays} days over</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <BankVsBooksPanel
        style={{ marginTop: 16 }}
        bank={bank}
        overview={bankView}
        openIds={openBank}
        onToggle={toggleBank}
        onGoSettings={onGoSettings}
        onRetry={bank.refresh}
        renderDetails={(row) => <UnmatchedLists ctx={ctx} accountId={row.accountId} row={row} />}
      />
    </div>
  );
}
